import { Handle, Position } from 'reactflow';
import { Box, Flex, Text, IconButton, HStack, Tag, VStack } from '@chakra-ui/react';
import { FiEdit, FiCopy, FiTrash2 } from 'react-icons/fi';
import { BsWhatsapp } from 'react-icons/bs';

const CallWhatsappFlowNode = ({ data }) => {
  const kvCount = (data.kvPairs || []).filter((p) => p.key).length;
  const timeoutEnabled = data.timeoutEnabled || false;

  const timeoutLabel = timeoutEnabled
    ? `${data.timeoutWaitTime || 30}${
        data.timeoutWaitUnit === 'hours' ? 'h' : data.timeoutWaitUnit === 'days' ? 'd' : 'm'
      }`
    : null;

  return (
    <Box
      bg="white"
      border="2px solid"
      borderColor="#25D366"
      borderRadius="xl"
      boxShadow={data.selected ? 'xl' : 'lg'}
      minW="240px"
      maxW="280px"
      position="relative"
    >
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: '#25D366', width: 10, height: 10 }}
      />

      {/* Header */}
      <Flex
        bgGradient="linear(to-r, #128C7E, #25D366)"
        color="white"
        px={4}
        py={2.5}
        borderTopRadius="xl"
        align="center"
        gap={2}
      >
        <BsWhatsapp size={16} />
        <Text noOfLines={1} flex={1} fontSize="sm" fontWeight="medium">
          WhatsApp Flow
        </Text>
        <HStack spacing={1}>
          <IconButton
            icon={<FiEdit size={14} />}
            size="xs"
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.300' }}
            onClick={(e) => { e.stopPropagation(); data.onEdit?.(data.id); }}
            aria-label="Edit"
          />
          <IconButton
            icon={<FiCopy size={14} />}
            size="xs"
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.300' }}
            onClick={(e) => { e.stopPropagation(); data.onDuplicate?.(data.id); }}
            aria-label="Duplicate"
          />
          <IconButton
            icon={<FiTrash2 size={14} />}
            size="xs"
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.300' }}
            onClick={(e) => { e.stopPropagation(); data.onDelete?.(data.id); }}
            aria-label="Delete"
          />
        </HStack>
      </Flex>

      {/* Body */}
      <Box p={3.5}>
        <Text fontSize="sm" fontWeight="medium" mb={2} noOfLines={1}>
          {data.label || 'WhatsApp Flow'}
        </Text>

        <VStack spacing={1.5} align="stretch">
          {/* Flow ID */}
          {data.flowId ? (
            <HStack
              spacing={1.5}
              bg="green.50"
              borderRadius="md"
              px={2}
              py={1}
              border="1px solid"
              borderColor="green.200"
            >
              <Text fontSize="xs" color="gray.500" flexShrink={0}>Flow:</Text>
              <Text fontSize="xs" fontWeight="medium" color="green.700" noOfLines={1} fontFamily="mono">
                {data.flowId}
              </Text>
            </HStack>
          ) : (
            <HStack
              spacing={1.5}
              bg="gray.50"
              borderRadius="md"
              px={2}
              py={1}
              border="1px dashed"
              borderColor="gray.300"
            >
              <Text fontSize="xs" color="gray.400">No flow ID set</Text>
            </HStack>
          )}

          {/* Body text */}
          {data.bodyText ? (
            <Box bg="gray.50" borderRadius="md" px={2} py={1} border="1px solid" borderColor="gray.200">
              <Text fontSize="xs" color="gray.600" noOfLines={2}>
                {data.bodyText}
              </Text>
            </Box>
          ) : (
            <Box bg="gray.50" borderRadius="md" px={2} py={1} border="1px dashed" borderColor="gray.300">
              <Text fontSize="xs" color="gray.400">No body text set</Text>
            </Box>
          )}

          {/* Badges */}
          {(kvCount > 0 || timeoutEnabled) && (
            <HStack spacing={2} flexWrap="wrap">
              {kvCount > 0 && (
                <Tag colorScheme="blue" variant="subtle" fontSize="10px">
                  {kvCount} param{kvCount !== 1 ? 's' : ''}
                </Tag>
              )}
              {timeoutEnabled && (
                <Tag colorScheme="orange" variant="subtle" fontSize="10px">
                  Timeout: {timeoutLabel}
                </Tag>
              )}
            </HStack>
          )}
        </VStack>
      </Box>

      {/* Output handles */}
      <Handle
        type="source"
        position={Position.Right}
        id="completed"
        style={{
          background: '#25D366',
          width: 10,
          height: 10,
          top: timeoutEnabled ? '40%' : '50%',
        }}
      />
      {timeoutEnabled && (
        <Handle
          type="source"
          position={Position.Right}
          id="timeout"
          style={{ background: '#F6AD55', width: 10, height: 10, top: '65%' }}
        />
      )}
    </Box>
  );
};

export default CallWhatsappFlowNode;
